import React, { useEffect, useRef, useState } from "react";
import {
  Bug,
  CheckCircle2,
  Lightbulb,
  MessageSquareWarning,
  Paperclip,
  Send,
  X,
} from "lucide-react";
import { bugReportsAPI } from "../../services/api";

const EMPTY_FORM = {
  reportType: "bug",
  title: "",
  description: "",
};

const TYPE_OPTIONS = [
  { value: "bug", label: "Bug", icon: Bug },
  { value: "issue", label: "Issue", icon: MessageSquareWarning },
  { value: "feature_request", label: "Feature Request", icon: Lightbulb },
];

export const ReportBugButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="Report a problem or request a feature"
    className="fixed bottom-20 left-3 z-[70] inline-flex items-center gap-2 rounded-full bg-slate-950 px-4 py-3 text-sm font-semibold text-white shadow-2xl ring-2 ring-orange-400/70 transition hover:-translate-y-0.5 hover:bg-black hover:ring-orange-300 focus:outline-none focus:ring-4 focus:ring-orange-300/50 sm:left-5 sm:px-5"
  >
    <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-white shadow-inner">
      <Bug className="h-5 w-5" />
      <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-slate-950 bg-red-500" />
    </span>
    <span className="hidden sm:inline">Report a Problem</span>
    <span className="sm:hidden">Report</span>
  </button>
);

const ReportBugModal = ({
  open,
  onClose,
  currentUser,
  activeProject,
  projectRole,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [screenshot, setScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setScreenshot(null);
    setSubmitting(false);
    setError("");
    setSuccess(null);
  }, [open]);

  if (!open) return null;

  const handleScreenshot = (file) => {
    setError("");
    if (!file) {
      setScreenshot(null);
      return;
    }

    const allowed = new Set(["image/png", "image/jpeg", "image/webp"]);
    if (!allowed.has(file.type)) {
      setError("Screenshot must be PNG, JPEG, or WEBP.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("Screenshot must be 8 MB or smaller.");
      return;
    }
    setScreenshot(file);
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    if (!form.title.trim() || !form.description.trim()) {
      setError("Title and description are required.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await bugReportsAPI.create({
        reportType: form.reportType,
        title: form.title.trim(),
        description: form.description.trim(),
        projectId: activeProject?.id || null,
        pageUrl: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        browserInfo: navigator.userAgent,
        screenSize: `${window.screen?.width || "?"}x${window.screen?.height || "?"}`,
        screenshot,
      });
      setSuccess(response?.data || {});
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Could not submit the report. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[1px]">
      <div className="flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b bg-gradient-to-r from-slate-950 to-slate-800 px-5 py-4 text-white">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-orange-500 p-2.5 shadow-lg">
              <Bug className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Report a Problem</h2>
              <p className="mt-0.5 text-xs text-slate-300">
                Bug, issue, or feature request
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-300 hover:bg-white/10 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {success ? (
          <div className="space-y-4 p-6 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Report submitted
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Reference: <strong>{success.report_number || "Saved"}</strong>
              </p>
              {success.email_warning ? (
                <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {success.email_warning}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-black"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="overflow-y-auto p-5">
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-gray-800">
                  Type
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {TYPE_OPTIONS.map(({ value, label, icon: Icon }) => {
                    const selected = form.reportType === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          setForm((prev) => ({ ...prev, reportType: value }))
                        }
                        className={`flex min-h-16 flex-col items-center justify-center gap-1 rounded-xl border px-2 py-2 text-xs font-semibold transition ${
                          selected
                            ? "border-orange-500 bg-orange-50 text-orange-700 ring-1 ring-orange-300"
                            : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-gray-800">
                  Title
                </span>
                <input
                  value={form.title}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, title: e.target.value }))
                  }
                  maxLength={255}
                  placeholder="Briefly describe what happened"
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-gray-800">
                  Description
                </span>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={6}
                  placeholder="Explain what you were doing, what happened, and what you expected."
                  className="w-full resize-y rounded-xl border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                />
              </label>

              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                      <Paperclip className="h-4 w-4 text-orange-600" />
                      Screenshot{" "}
                      <span className="font-normal text-gray-400">
                        (optional)
                      </span>
                    </div>
                    <p className="mt-1 truncate text-xs text-gray-500">
                      {screenshot?.name || "PNG, JPEG, or WEBP · max 8 MB"}
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => handleScreenshot(e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="shrink-0 rounded-lg border bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:border-orange-300 hover:bg-orange-50"
                  >
                    {screenshot ? "Change" : "Attach"}
                  </button>
                </div>
              </div>

              <div className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-600">
                <div>
                  <strong>User:</strong> {currentUser?.email || "Current user"}
                </div>
                <div className="mt-1">
                  <strong>Project:</strong>{" "}
                  {activeProject?.name || "Global / no project"}
                  {projectRole ? ` · ${projectRole.replaceAll("_", " ")}` : ""}
                </div>
              </div>

              {error ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              ) : null}
            </div>

            <div className="mt-5 flex justify-end gap-2 border-t pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg border px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 disabled:opacity-60"
              >
                <Send className="h-4 w-4" />
                {submitting ? "Submitting..." : "Submit Report"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReportBugModal;
